import { type ReactNode, type RefObject } from "react";

import { TableProps } from "@magnetic/table";
import type { GridApi } from "ag-grid-enterprise";

import { BaseColumnConfig } from "../tables/columnConfigs/baseColumnConfig";
import { TableData, TypedTableRendererType } from "../typedTableTypes";

export type BaseTypedTableProps<COLUMN> = {
  readonly tableType?: TypedTableRendererType; // optional, which kind of table to render
  readonly layout?:
    | BaseTypedTableLayoutProps // recommended, each table should define or reuse layout constraints per UX
    | ((
        api: GridApi,
        typedTableRef: RefObject<HTMLDivElement>
      ) => BaseTypedTableLayoutProps); // factory method

  readonly name: string;
  readonly columns: { [k in keyof COLUMN]: COLUMN[k] };
  readonly data?: TableData<COLUMN>[];
  readonly displayResultCount?: boolean;
  readonly displaySelectedCount?: boolean;
  readonly maxClientPage?: number;
  readonly className?: string;
  readonly flashOnUpdate?: boolean;
  readonly actions?: ReactNode[];
  readonly defaultSortColumn?: keyof COLUMN;
  // merges in almost all Magnetic table properties
} & Omit<TableProps<unknown>, "columns" | "data">;

/**
 * When linking row methods from other libraries, they should follow this convention to not break existing client code
 */
export interface MagneticCompatibleRowData<T> {
  original?: T;
}

/**
 * Client may override grid layout dimensions declaratively from code. Otherwise, custom CSS rules would be used
 */
export interface BaseTypedTableLayoutProps {
  width?: string | number;
  height?: string | number; // Note: height must be in CSS rule or in this property for virtual scrolling grid to render
  minHeight?: string | number;
}

export type Column<V extends BaseColumnConfig<VT>, VT> = {
  [k in string]: V;
};

/**
 * Clone logic from Magnetic is-table-filtered.ts
 * @param globalFilter
 */
export function isGlobalFilterApplied(
  globalFilter: object | string | Array<unknown>
): boolean {
  if (!globalFilter) {
    return false;
  }

  if (typeof globalFilter === "string") {
    return globalFilter.trim() !== "";
  }

  if (Array.isArray(globalFilter)) {
    return globalFilter.length > 0;
  }

  if (typeof globalFilter === "object") {
    return Object.values(globalFilter).some((value) =>
      isGlobalFilterApplied(value)
    );
  }

  return Boolean(globalFilter);
}

/**
 * To determine which table library implementation to render, rely on the developer setting switch
 */
export function getTypedTableRendererTypePreference(): TypedTableRendererType {
  return window.cnc.settings.get("agGridTypedTable")
    ? TypedTableRendererType.agGrid
    : TypedTableRendererType.magnetic;
}

/**
 * UX will have to refine the constraints for the minimum, base and maximum height in terms of a CSS expression
 */
const fullWidth = "100%";
const minHeight = "200px";

// the hidden layout is used to hide the grid before the factory computes actual layout space
export const typedGridHiddenLayout: BaseTypedTableLayoutProps = {
  width: fullWidth,
  minHeight: "0px",
  height: "0px"
};

export const typedGridDefaultLayout: BaseTypedTableLayoutProps = {
  width: fullWidth,
  minHeight,
  height: "50vh"
};

export const typedGridSmallLayout: BaseTypedTableLayoutProps = {
  width: fullWidth,
  minHeight,
  height: "250px"
};

export const typedGridMediumLayout: BaseTypedTableLayoutProps = {
  width: fullWidth,
  minHeight,
  height: "500px"
};

/**
 * utility should accurately calculate the available vertical space in the viewport.
 * It excludes content blocks above the table, and subtracts space occupied by footer.
 */
function TypedGridViewportLayoutFactory(
  verticalViewportPercentage: number,
  _api: GridApi,
  typedGridDivRef: RefObject<HTMLDivElement>
): BaseTypedTableLayoutProps {
  const typedGridDiv = typedGridDivRef.current!;
  const document = typedGridDiv?.ownerDocument ?? window.document;
  try {
    const gridBody = typedGridDiv.querySelector(".ag-body");
    const appFooter = document?.querySelector(
      ".cnc-shell > .cnc-page-layout > .cnc-content-wrapper > footer"
    );

    const excludeContentAboveAndBelowGrid =
      (gridBody?.getBoundingClientRect().top ?? 0) +
      (appFooter?.getBoundingClientRect()?.height ?? 0);

    return {
      width: fullWidth,
      minHeight,
      height: `calc( (${verticalViewportPercentage}vh - ${excludeContentAboveAndBelowGrid}px)`
    };
  } catch {
    return typedGridDefaultLayout;
  }
}

/**
 * Fabric list table uses a single grid inside a content container
 * Allocate the viewport's full 100% vertical "available space"
 */
export function TypedGridViewport100LayoutFactory(
  api: GridApi,
  typedGridDivRef: RefObject<HTMLDivElement>
): BaseTypedTableLayoutProps {
  return TypedGridViewportLayoutFactory(100, api, typedGridDivRef);
}

/**
 * Allocate grid to use as much or little vertical space as the number of rows
 * Tables like the Assembly list for a fabric deployment have several required parts "stacked" tables
 * It looked bad when so much wasted space was used
 */
export function TypedGridAutoHeightLayoutFactory(
  api: GridApi,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _typedGridDivRef: RefObject<HTMLDivElement>
): BaseTypedTableLayoutProps {
  api.setGridOption("domLayout", "autoHeight");

  return {
    width: fullWidth,
    minHeight: "auto",
    height: "auto"
  };
}
